import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { ProgressSpinner } from 'primereact/progressspinner'

import { calculateBreakpointWidth } from '../../../../../../utils/helper/calculateBreakpointWidth'
import { SectionForm, SectionResult } from './sections'

type FormFieldName = 'attachment' | 'category'

type Props = {
  formik: FormikProps<any>
  isRequestRunning: boolean
  isFormFieldValid: (name: FormFieldName) => boolean
  handleClose: () => void
  visible: boolean
  setVisible: React.Dispatch<React.SetStateAction<boolean>>
  getFormErrorMessage: (name: FormFieldName) => false | JSX.Element
  isChecking: boolean
  showResult: boolean
  isUploadFileVisible: boolean
  setShowResult: React.Dispatch<React.SetStateAction<boolean>>
}

export const UploadAttachmentDialog: React.FC<Props> = ({
  formik,
  isRequestRunning,
  isFormFieldValid,
  handleClose,
  visible,
  setVisible,
  getFormErrorMessage,
  isChecking,
  showResult,
  isUploadFileVisible,
  setShowResult,
}) => {
  const { t } = useTranslation()

  // header
  const header = (
    <>{!isChecking && t(`invoice.attachments.table.upload.dialog.title`)}</>
  )

  // footer
  const uploadButtonLabel =
    formik.values.category === 'aiInvoiceDocument'
      ? t('invoice.attachments.table.upload.dialog.form.uploadWithAi.label')
      : t('invoice.attachments.table.upload.dialog.form.upload.label')

  const footer = (
    <>
      <Button
        label={t(`common.buttons.cancel`)}
        icon="pi pi-times"
        className="p-button-text w-full md:w-auto mb-1 md:mb-0"
        onClick={handleClose}
        visible={!isChecking}
      />
      <Button
        label={uploadButtonLabel}
        icon="pi pi-check"
        className="w-full md:w-auto"
        onClick={formik.submitForm}
        type="submit"
        disabled={!formik.dirty || isRequestRunning}
        visible={!isChecking}
      />
    </>
  )

  // Wenn API endpunkt mit checkwithai auf true setzen, dann wird das so hochgeladen!
  const sectionChecking = (
    <>
      <div className="flex justify-content-center align-items-center">
        <ProgressSpinner
          strokeWidth="8"
          fill="var(--surface-ground)"
          animationDuration=".5s"
        />
      </div>
      <h3 className="flex justify-content-center align-items-center">
        {t('invoice.attachments.table.checking.dialog.loading')}
      </h3>
    </>
  )

  return (
    <>
      <Button
        label={t('invoice.attachments.table.upload.label')}
        onClick={() => setVisible(true)}
        icon="pi pi-upload"
        className="p-button-outlined mb-2"
      />

      <Dialog
        visible={visible}
        style={{ width: '45rem' }}
        breakpoints={{ [calculateBreakpointWidth(45)]: '90vw' }}
        header={header}
        footer={footer}
        onHide={handleClose}
        blockScroll
      >
        {isUploadFileVisible && (
          <SectionForm
            formik={formik}
            isFormFieldValid={isFormFieldValid}
            getFormErrorMessage={getFormErrorMessage}
          />
        )}

        {(isChecking || isRequestRunning) && sectionChecking}
      </Dialog>
      {showResult && (
        <SectionResult setVisible={setShowResult} visible={showResult} />
      )}
    </>
  )
}
